import React, { useEffect, useState } from "react";
import client from "../api/client";

interface PromoCodeRow {
  id: number;
  code: string;
  influencerId: number;
  influencerUsername: string | null;
  discountPercent: number;
  active: boolean;
  createdAt: string;
  expiresAt: string;
  salesCount: number;
}

interface PromoSale {
  id: number;
  username: string | null;
  userId: number | null;
  amount: number;
  currency: string;
  paymentType: string;
  bank: string;
  createdAt: string;
  manager: string;
}

const PAYMENT_TYPE_LABEL: Record<string, string> = {
  full_payment: "Полная",
  prepayment: "Предоплата",
  postpayment: "Постоплата",
  express_print: "Эксп. печать",
  express_edit: "Уск. ред.",
  urep: "УРЭП",
  delivery: "Доставка",
  additional: "Доп.",
};

const fmtDate = (iso: string) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

const fmtDateTime = (iso: string) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusOf = (r: PromoCodeRow): { label: string; cls: string } => {
  if (!r.active) return { label: "деактив.", cls: "pct__status pct__status--inactive" };
  if (new Date(r.expiresAt).getTime() <= Date.now()) {
    return { label: "истёк", cls: "pct__status pct__status--expired" };
  }
  return { label: "активен", cls: "pct__status pct__status--active" };
};

interface PromoCodesTableProps {
  /** Свёрнутый режим для /marketing: таблица спрятана за тогл-заголовком,
   *  данные грузятся только после первого раскрытия. */
  collapsible?: boolean;
}

const PromoCodesTable: React.FC<PromoCodesTableProps> = ({ collapsible = false }) => {
  const [rows, setRows] = useState<PromoCodeRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(!collapsible);
  const [activatingId, setActivatingId] = useState<number | null>(null);

  // Попап продаж по выбранному промокоду.
  const [openCode, setOpenCode] = useState<PromoCodeRow | null>(null);
  const [popupSales, setPopupSales] = useState<PromoSale[] | null>(null);
  const [popupLoading, setPopupLoading] = useState(false);
  const [popupError, setPopupError] = useState<string | null>(null);

  useEffect(() => {
    if (!expanded) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await client.get<PromoCodeRow[]>("/admin/promo-codes");
        if (alive) setRows(data);
      } catch (e: any) {
        if (alive) setError(e?.message || "Ошибка");
      }
    })();
    return () => {
      alive = false;
    };
  }, [expanded]);

  const activate = async (row: PromoCodeRow) => {
    setActivatingId(row.id);
    try {
      const { data } = await client.patch<PromoCodeRow>(`/admin/promo-codes/${row.id}/activate`);
      // Бэк мог погасить другой активный код этого же инфлюенсера —
      // отражаем это локально, не перезагружая всю таблицу.
      setRows((prev) =>
        prev
          ? prev.map((r) => {
              if (r.id === row.id) return { ...r, active: true, expiresAt: data.expiresAt ?? r.expiresAt };
              if (r.influencerId === row.influencerId && r.active) return { ...r, active: false };
              return r;
            })
          : prev
      );
    } catch (e: any) {
      setError(e?.message || "Не удалось активировать");
    } finally {
      setActivatingId(null);
    }
  };

  const openSales = async (row: PromoCodeRow) => {
    setOpenCode(row);
    setPopupSales(null);
    setPopupError(null);
    setPopupLoading(true);
    try {
      const { data } = await client.get<PromoSale[]>(`/admin/promo-codes/${row.id}/sales`);
      setPopupSales(data);
    } catch (e: any) {
      setPopupError(e?.message || "Ошибка");
    } finally {
      setPopupLoading(false);
    }
  };

  const closeSales = () => {
    setOpenCode(null);
    setPopupSales(null);
    setPopupError(null);
  };

  return (
    <div className="pct">
      <style>{styles}</style>
      {collapsible ? (
        <button
          type="button"
          className="pct__toggle"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          <span className={"pct__chevron" + (expanded ? " pct__chevron--open" : "")}>▸</span>
          Промокоды
        </button>
      ) : (
        <h2 className="pct__title">Промокоды</h2>
      )}

      {expanded && error && <div className="pct__error">{error}</div>}
      {expanded && !rows && !error && <div className="pct__hint">Загрузка…</div>}
      {expanded && rows && rows.length === 0 && <div className="pct__hint">Пока нет промокодов.</div>}

      {expanded && rows && rows.length > 0 && (
        <div className="pct__table-wrap">
          <table className="pct__table">
            <thead>
              <tr>
                <th>Код</th>
                <th>Инфлюенсер</th>
                <th>Скидка</th>
                <th>Создан</th>
                <th>Истекает</th>
                <th>Статус</th>
                <th className="pct__num">Продаж</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const st = statusOf(r);
                return (
                  <tr key={r.id}>
                    <td>
                      <button
                        type="button"
                        className="pct__code-btn"
                        onClick={() => openSales(r)}
                        title="Показать продажи по этому коду"
                      >
                        {r.code}
                      </button>
                    </td>
                    <td>{r.influencerUsername || `#${r.influencerId}`}</td>
                    <td>{r.discountPercent}%</td>
                    <td>{fmtDate(r.createdAt)}</td>
                    <td>{fmtDate(r.expiresAt)}</td>
                    <td>
                      <span className={st.cls}>{st.label}</span>
                      {/* Кнопка у всех неактивных: и «деактив.», и «истёк»
                          (истёкшему бэк продлит срок на 30 дней от активации). */}
                      {(!r.active || new Date(r.expiresAt).getTime() <= Date.now()) && (
                        <button
                          type="button"
                          className="pct__activate-btn"
                          disabled={activatingId === r.id}
                          onClick={() => activate(r)}
                          title="Снова сделать код активным (истёкшему срок продлится на 30 дней; другой активный код этого инфлюенсера будет деактивирован)"
                        >
                          {activatingId === r.id ? "…" : "Активировать"}
                        </button>
                      )}
                    </td>
                    <td className="pct__num">{r.salesCount}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openCode && (
        <div className="pct__overlay" onClick={closeSales}>
          <div className="pct__modal" onClick={(e) => e.stopPropagation()}>
            <div className="pct__modal-head">
              <div>
                <div className="pct__modal-eyebrow">Продажи по промокоду</div>
                <div className="pct__modal-title">
                  <span className="pct__code">{openCode.code}</span>
                  <span className="pct__modal-meta">
                    {' · '}{openCode.influencerUsername || `#${openCode.influencerId}`}
                    {' · '}{openCode.discountPercent}%
                  </span>
                </div>
              </div>
              <button className="pct__modal-close" onClick={closeSales} aria-label="Закрыть">×</button>
            </div>

            {popupLoading && <div className="pct__hint">Загрузка…</div>}
            {popupError && <div className="pct__error">{popupError}</div>}
            {popupSales && popupSales.length === 0 && (
              <div className="pct__hint">Продаж по этому коду нет.</div>
            )}
            {popupSales && popupSales.length > 0 && (
              <div className="pct__modal-table-wrap">
                <table className="pct__table">
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Сумма</th>
                      <th>Тип</th>
                      <th>Счёт</th>
                      <th>Менеджер</th>
                      <th>Дата</th>
                    </tr>
                  </thead>
                  <tbody>
                    {popupSales.map((s) => (
                      <tr key={s.id}>
                        <td>{s.username || `#${s.userId ?? "—"}`}</td>
                        <td className="pct__num">{s.amount?.toLocaleString("ru-RU")} {s.currency}</td>
                        <td>{PAYMENT_TYPE_LABEL[s.paymentType] || s.paymentType}</td>
                        <td>{s.bank}</td>
                        <td>{s.manager || "—"}</td>
                        <td>{fmtDateTime(s.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = `
.pct { margin: 24px 0; padding: 0 8px; }
.pct__title { font-size: 16px; font-weight: 600; margin: 0 0 12px; }
.pct__hint { color: var(--text-3, #888); font-size: 13px; padding: 8px 0; }
.pct__error { color: #c33; font-size: 13px; padding: 8px 0; }
.pct__table-wrap { border: 1px solid var(--border, #e5e5e5); border-radius: 8px; overflow: hidden; background: #fff; }
.pct__table { width: 100%; border-collapse: collapse; font-size: 13px; }
.pct__table th, .pct__table td { padding: 8px 12px; text-align: left; border-bottom: 1px solid var(--border, #eee); }
.pct__table th { background: #fafafa; font-weight: 500; color: var(--text-2, #555); }
.pct__table tr:last-child td { border-bottom: none; }
.pct__code { font-family: Menlo, Consolas, monospace; letter-spacing: 0.03em; }
.pct__code-btn {
  font-family: Menlo, Consolas, monospace;
  letter-spacing: 0.03em;
  font-size: inherit;
  background: none;
  border: none;
  padding: 0;
  color: #1d4ed8;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.pct__code-btn:hover { color: #1e40af; }
.pct__num { text-align: right; font-variant-numeric: tabular-nums; }
.pct__status { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; }
.pct__status--active { background: #dcfce7; color: #166534; }
.pct__status--expired { background: #fef3c7; color: #92400e; }
.pct__status--inactive { background: #f1f5f9; color: #64748b; }
.pct__toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 600;
  color: inherit;
  cursor: pointer;
}
.pct__chevron { display: inline-block; transition: transform 0.15s ease; font-size: 13px; }
.pct__chevron--open { transform: rotate(90deg); }
.pct__activate-btn {
  margin-left: 8px;
  padding: 2px 10px;
  border: 1px solid #16a34a;
  border-radius: 999px;
  background: none;
  color: #16a34a;
  font-size: 11px;
  cursor: pointer;
}
.pct__activate-btn:hover { background: #f0fdf4; }
.pct__activate-btn:disabled { opacity: 0.5; cursor: default; }

.pct__overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.pct__modal {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 820px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.22);
}
.pct__modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--border, #eee);
}
.pct__modal-eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 11px;
  color: var(--text-3, #888);
  margin-bottom: 2px;
}
.pct__modal-title { font-size: 15px; font-weight: 500; }
.pct__modal-meta { color: var(--text-3, #888); font-weight: 400; }
.pct__modal-close {
  background: none;
  border: none;
  font-size: 22px;
  line-height: 1;
  color: var(--text-3, #888);
  cursor: pointer;
  padding: 0 4px;
}
.pct__modal-close:hover { color: var(--text, #111); }
.pct__modal-table-wrap { overflow-y: auto; flex: 1; }
.pct__modal-table-wrap .pct__table thead th {
  position: sticky;
  top: 0;
}
`;

export default PromoCodesTable;
